/**
 * SignupPage
 * ==========
 *
 * Email + password sign-up via Supabase. Two possible flows depending
 * on project config:
 *
 *  - If email confirmation is OFF (Supabase dashboard -> Auth -> Email
 *    settings), ``signUp`` returns a usable session immediately, and
 *    we behave just like login.
 *  - If email confirmation is ON, ``signUp`` returns ``user`` without a
 *    session; the user must click the link in their inbox before they
 *    can sign in. We show a "check your inbox" success screen.
 *
 * The backend doesn't care which mode is enabled — the JIT user-row
 * creation in ``supabase_auth.py`` fires on first authenticated request,
 * not on signup.
 */
import { useState, FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../auth/supabase";
import AuthShell from "../components/AuthShell";

export default function SignupPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsConfirm, setNeedsConfirm] = useState(false);
  const nav = useNavigate();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { data, error } = await supabase.auth.signUp({ email, password });
    setBusy(false);
    if (error) {
      setError(error.message);
      return;
    }
    if (data.session) {
      // Confirmation disabled -> we're signed in immediately.
      nav("/uploads", { replace: true });
    } else {
      // Confirmation required.
      setNeedsConfirm(true);
    }
  }

  if (needsConfirm) {
    return (
      <AuthShell title="Check your inbox">
        <p className="text-sm text-slate-600">
          We sent a confirmation link to <strong>{email}</strong>. Click the link to
          activate your account, then return here to sign in.
        </p>
        <div className="mt-4">
          <Link to="/login" className="btn-primary w-full text-center">
            Back to sign in
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Create your account">
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label">Email</label>
          <input className="input" type="email" autoComplete="email"
                 value={email} onChange={(e) => setEmail(e.target.value)}
                 required />
        </div>
        <div>
          <label className="label">Password</label>
          <input className="input" type="password" autoComplete="new-password"
                 value={password} onChange={(e) => setPassword(e.target.value)}
                 minLength={6} required />
          <p className="text-xs text-slate-500 mt-1">Minimum 6 characters.</p>
        </div>
        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        <button className="btn-primary w-full" disabled={busy} type="submit">
          {busy ? "Creating account…" : "Sign up"}
        </button>
      </form>
      <div className="mt-4 text-xs text-slate-500 text-center">
        Already have an account? <Link to="/login" className="hover:text-accent-600">Sign in</Link>
      </div>
    </AuthShell>
  );
}
